import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { Pencil, Trash } from 'phosphor-react'

import { Wrapper } from 'components'
import { RouteNamesAuth } from 'routes/enum'
import { campaignService } from 'api/services/campaign'
import { CampaignType, SaveCampaignBody } from 'api/types/campaign'
import { format } from 'date-fns'

// form validation schema
const validationSchema = Yup.object().shape({
  title: Yup.string().required('Obrigatório'),
})

export const BackOfficeCampaigns = () => {
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  // track selected campaign for deletion
  const [selected, setSelected] = useState<string>('')

  // campaigns data
  const [data, setData] = useState<CampaignType[] | null>(null)

  //modals visibility
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)

  // fetch campaign list from API
  const getCampaignList = async () => {
    setIsLoading(true)
    try {
      const response = await campaignService.getCampaignList()
      setData(response as CampaignType[])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar as campanhas.')
    }
  }

  // delete selected product
  const deleteCampaign = async () => {
    if (!selected) return
    try {
      setIsApiBeingCalled(true)
      await campaignService.deleteCampaign({
        id: selected,
      })
      setShowDeleteModal(false)
      getCampaignList()
      setIsApiBeingCalled(false)
      toast.success('Campanha apagada com sucesso.')
    } catch (error) {
      toast.error('Não foi possível apagar a campanha.')
    }
  }

  // change campaign status
  const changeCampaignStatus = async (id: number, active: boolean) => {
    try {
      setIsApiBeingCalled(true)
      const campaignToUpdate = data?.find(c => c.id === id)
      if (!campaignToUpdate) throw new Error('Campanha não encontrada.')

      const updatedCampaign: SaveCampaignBody = {
        id: campaignToUpdate.id.toString(),
        title: campaignToUpdate.title,
        content: campaignToUpdate.content,
        description: campaignToUpdate.description,
        label: campaignToUpdate.label,
        link: campaignToUpdate.link,
        createdDate: campaignToUpdate.createdDate,
        expirationDate: campaignToUpdate.expirationDate,
        active: active,
      }

      await campaignService.saveCampaign(updatedCampaign)
      getCampaignList()
      setIsApiBeingCalled(false)
      toast.success('Status da campanha atualizado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível atualizar o status da campanha.')
    }
  }

  // formik for create campaign form
  const formik = useFormik({
    initialValues: {
      title: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async () => {
      setIsApiBeingCalled(true)

      try {
        const newCampaign: SaveCampaignBody = {
          title: formik.values.title,
        }
        const response = await campaignService.saveCampaign(newCampaign)

        try {
          const campaignId = JSON.parse(response as string).id
          setShowCreateModal(false)
          setIsApiBeingCalled(false)
          navigate(
            RouteNamesAuth.BACKOFFICE_CAMPAIGN_DETAILS.replace(
              ':id',
              campaignId,
            ),
          )
        } catch (error) {
          console.error('Error parsing campaign ID:', error)
        }

        toast.success('Campanha criada com sucesso.')
      } catch (error) {
        toast.error('Não foi possível criar a campanha.')
      }
    },
  })

  // initial data fetch
  useEffect(() => {
    getCampaignList()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
          <span>Campanhas</span>
          <Button
            variant="default"
            color="secondary"
            onClick={() => {
              setShowCreateModal(true)
            }}
          >
            Adicionar Nova Campanha
          </Button>
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campanha</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data de Criação</TableHead>
                  <TableHead>Data de Expiração</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data &&
                  data.map((campaign: CampaignType, index: number) => (
                    <TableRow
                      key={`${campaign.id}-${index}`}
                      className="even:bg-primary-50/25"
                    >
                      <TableCell className="normal-case">
                        {campaign.title}
                      </TableCell>
                      <TableCell className="normal-case">
                        <span className="flex items-center gap-2">
                          {campaign.active ? (
                            <Badge
                              className="pointer-events-none"
                              color="success"
                              variant="base"
                            >
                              Ativa
                            </Badge>
                          ) : (
                            <Badge
                              className="pointer-events-none"
                              color="error"
                              variant="base"
                            >
                              Inativo
                            </Badge>
                          )}
                          <Switch
                            checked={campaign.active}
                            onCheckedChange={() => {
                              changeCampaignStatus(
                                campaign.id,
                                !campaign.active,
                              )
                            }}
                            className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                          />
                        </span>
                      </TableCell>
                      <TableCell className="normal-case">
                        {format(new Date(campaign.createdDate), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell className="normal-case">
                        {campaign.expirationDate
                          ? format(
                              new Date(campaign.expirationDate),
                              'dd/MM/yyyy',
                            )
                          : '--'}
                      </TableCell>
                      <TableCell className="normal-case">
                        {campaign.description}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="default"
                            color="secondary"
                            onClick={() => {
                              navigate(
                                RouteNamesAuth.BACKOFFICE_CAMPAIGN_DETAILS.replace(
                                  ':id',
                                  campaign.id.toString(),
                                ),
                              )
                            }}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="default"
                            color="error"
                            disabled={isApiBeingCalled}
                            onClick={() => {
                              setSelected(campaign.id.toString())
                              setShowDeleteModal(true)
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Campanha</ModalTitle>
            <ModalDescription>
              Quer apagar esta campanha?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados associados a esta campanha serão perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteCampaign()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showCreateModal} onOpenChange={setShowCreateModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Criar Campanha</ModalTitle>
            <ModalDescription>
              <div className="space-y-4">
                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Título
                  </h3>
                  <div>
                    <Input
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
              </div>
              <br />
              <br />
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              onClick={() => formik.handleSubmit()}
            >
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
